import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { GENDER } from "../../../../constants/master";
import {
  STAFF_USER_FORM_INITIAL_VALUES,
  StaffUser,
} from "../../../../constants/staff-user-schema";
import { EMAIL_LOCKED_HELPER_TEXT } from "../../../../constants/user-constant";
import { useLocationsList } from "../../../../queries/use-locationList";
import { useProfileAvatar } from "../../../../queries/use-profile-avatar";
import {
  convertToBase64,
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../utils/utils";
import CustomSelect from "../../../CustomSelect";
import Input from "../../../Input";
import MaskInput from "../../../MaskInput";
import MaskMultiSelectScroll from "../../../MaskMultiSelectScroll";
import SideSheetDrawer from "../../../SidesheetDrawer";
import DropZone from "../../DropZone";
import CustomDatePicker from "../../../CustomDatePicker";
import * as Yup from "yup";
import dayjs from "dayjs";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import useRoleOptions from "../../settings/user-settings/use-role-options";
import { getViewAs } from "../../../../utils/auth-storage";

interface StaffUserDrawerProps {
  open: boolean;
  modalTitle:
    "Edit Staff User" | "Add Staff User" | "Edit User" | "Add New User";
  initialValues?: StaffUser;
  onClose: () => void;
  onSubmit: (values: StaffUser) => Promise<any>;
}

const UpsertStaffUserDrawer = ({
  open,
  modalTitle,
  onClose,
  initialValues,
  onSubmit,
}: StaffUserDrawerProps) => {
  /* Every title the union allows is either an "Add …" or an "Edit …", so the
     prefix is the mode — there is no separate mode prop. */
  const isEditMode = modalTitle.startsWith("Edit");

  const alphabetsOnly = function (
    this: Yup.TestContext,
    value: string | undefined
  ) {
    if (!value) return true;
    if (/^[a-zA-Z]+$/.test(value)) return true;
    const label = (this.schema as Yup.StringSchema).spec.label ?? this.path;
    return this.createError({
      message: `${label} should only contain alphabets`,
    });
  };

  const STAFF_USER_FORM_VALIDATION_SCHEMA = Yup.object({
    phone: Yup.string().min(12, "Contact Number must be at least 8 digits"),
    firstName: Yup.string()
      .required()
      .label("First Name")
      .test("alphabetsOnly", alphabetsOnly),
    gender: Yup.string().required().label("Gender"),
    lastName: Yup.string()
      .required()
      .label("Last Name")
      .test("alphabetsOnly", alphabetsOnly),
    email: Yup.string()
      .email()
      .matches(/.*@.+\..+/, "Email address must be a valid Email")
      .required()
      .label("Email"),
    role: Yup.string().nonNullable().required().label("Role"),
    workLocations: Yup.array()
      .when("modalTitle", {
        is: () =>
          modalTitle === "Add Staff User" || modalTitle === "Edit Staff User",
        then: (schema) =>
          schema.min(1, "Location is a required field").required(),
        otherwise: (schema) => schema.notRequired(),
      })
      .label("Location"),
  });
  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    setFieldValue,
    errors,
    touched,
  } = useFormik({
    initialValues: initialValues ?? STAFF_USER_FORM_INITIAL_VALUES,
    validationSchema: STAFF_USER_FORM_VALIDATION_SCHEMA,
    onSubmit: handleSubmitValues,
    enableReinitialize: true,
  });

  const params = useParams();
  const viewAs = getViewAs();
  const showSnackbar = useSnackbar();
  const [locationPagination, setLocationPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: providerGroupLocationList, isFetching: isLocationFetching }: any =
    useLocationsList({
      pagination: locationPagination,
      status: true,
      providerGroupId:
        viewAs === "provider" ? getXtenantId() : params?.providerGroupId,
      enabled: open,
    });

  const locationOptions = useMemo(() => {
    const fetched =
      providerGroupLocationList?.data?.content?.map((l: any) => ({
        value: l?.uuid,
        label: l?.name,
      })) ?? [];
    const selected = (values.workLocations ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o: { [k: string]: string }) => o[idKey] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [providerGroupLocationList, values.workLocations]);

  const queryClient = useQueryClient();
  const { mutateAsync: updateAvatar } = useProfileAvatar();

  function handleSubmitValues(v: any) {
    onSubmit(v)
      .then((res) => {
        if (!values?.avatar?.includes(".amazonaws.com")) {
          updateAvatar({
            newAvatar: { newAvatar: values.avatar, userId: values.uuid },
            xTENANTID: params?.providerGroupId,
          });
        }

        showSnackbar(getSuccessSnackbar(res));
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ["provider-group-users-list"],
          });
          queryClient.invalidateQueries({
            queryKey: ["user-staff-profile"],
          });
          closeAndReset();
        }, 0);
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  }

  function closeAndReset() {
    resetForm();
    onClose();
  }

  const { rolesData: rolesList } = useRoleOptions({
    enabled: open,
    pgiD: params?.providerGroupId ?? "",
    roleView: "staff",
  });

  return (
    <SideSheetDrawer
      onClose={closeAndReset}
      open={open}
      title={modalTitle}
      w="55vw"
      footerActions={
        <Button
          onClick={() => handleSubmit()}
          variant="contained"
          style={{ width: "100px" }}
        >
          Save
        </Button>
      }
    >
      <Grid container width="100%" spacing={2}>
        <Grid container item md={12} spacing={2}>
          <Grid item sm={12} md={6} lg={4} xl={4}>
            <DropZone
              accept={".jpg,.jpeg,.png"}
              label="Profile Photo"
              content="Upload Profile Photo"
              value={values?.avatar}
              height={"190px"}
              backgroundColor={"primary.light"}
              uploadIcon={
                <AddPhotoAlternateOutlinedIcon sx={{ fontSize: "4rem" }} />
              }
              onClick={async (files) => {
                if (files) {
                  const base64File = await convertToBase64(files[0]);
                  setFieldValue("avatar", base64File);
                } else setFieldValue("avatar", null);
              }}
            />
          </Grid>
        </Grid>
        <Grid item sm={12} md={6} lg={4} xl={4}>
          <Input
            required
            label="First Name"
            textFieldProps={{
              name: "firstName",
              value: values.firstName,
              onBlur: handleBlur,
              onChange: handleChange,
              placeholder: "Enter First Name",
            }}
            error={touched?.firstName && errors?.firstName}
          />
        </Grid>
        <Grid item sm={12} md={6} lg={4} xl={4}>
          <Input
            required
            label="Last Name"
            textFieldProps={{
              name: "lastName",
              value: values.lastName,
              onBlur: handleBlur,
              onChange: handleChange,
              placeholder: "Enter Last Name",
            }}
            error={touched?.lastName && errors?.lastName}
          />
        </Grid>
        <Grid item sm={12} md={6} lg={4} xl={4}>
          <CustomDatePicker
            value={values.birthDate}
            label="DOB"
            maxDate={dayjs()}
            onChange={(val) => {
              setFieldValue("birthDate", val);
            }}
          />
        </Grid>
        <Grid item sm={12} md={6} lg={4} xl={4}>
          <CustomSelect
            required
            label="Gender"
            options={GENDER}
            value={values.gender}
            onChange={(option) => setFieldValue("gender", option)}
            error={touched.gender && errors.gender}
          />
        </Grid>
        <Grid item sm={12} md={6} lg={4} xl={4}>
          <Input
            label="Email"
            textFieldProps={{
              name: "email",
              value: values.email,
              onBlur: handleBlur,
              onChange: handleChange,
              placeholder: "Enter Email",
              disabled: isEditMode,
              helperText: isEditMode ? EMAIL_LOCKED_HELPER_TEXT : undefined,
            }}
            required
            error={touched?.email && errors?.email}
          />
        </Grid>

        <Grid item sm={12} md={6} lg={4} xl={4}>
          <MaskInput
            mask={"(999) 999-9999"}
            label="Contact Number"
            name="phone"
            error={errors?.phone}
            // required
            placeholder="Enter Contact Number"
            value={values.phone}
            onChange={(e) => setFieldValue("phone", e.target.value)}
          />
        </Grid>
        <Grid item sm={12} md={6} lg={4} xl={4}>
          <CustomSelect
            name="role"
            required
            label="Role"
            handleBlur={handleBlur}
            options={rolesList}
            value={values.role}
            onChange={(val) => setFieldValue("role", val)}
            placeholder="Select Role"
            error={touched.role && errors?.role}
          />
        </Grid>
        {(modalTitle === "Edit Staff User" ||
          modalTitle === "Add Staff User") && (
          <Grid item sm={12} md={6} lg={4} xl={4}>
            <MaskMultiSelectScroll
              required
              label="Location"
              options={locationOptions}
              optionWithSelectAll
              optionWithCheckbox
              value={values.workLocations ?? undefined}
              onChange={(v) => setFieldValue("workLocations", v)}
              error={touched?.workLocations && errors?.workLocations}
              pagination={locationPagination}
              setPagination={setLocationPagination}
              totalElements={providerGroupLocationList?.data?.page?.totalElements}
              loading={isLocationFetching}
            />
          </Grid>
        )}
      </Grid>
    </SideSheetDrawer>
  );
};

export default UpsertStaffUserDrawer;
